import { Tooltip, Whisper } from 'rsuite';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { LoadingSpinerLocal } from '../../../../../../components/LoadingSpinner';
import { formatNumberWithThousandsSeparator } from '../../utils';
import styles from './ElementsRelatedToPhysicalChannelChart.module.scss';

export type ElementsRelatedToPhysicalChannelChartProps = {
  title?: string;
  className?: string;
  data?: any;
  isLoading?: boolean;
};

const GAUGE_MIN = 0;
const GAUGE_MAX = 200;
const GAUGE_TICK_INTERVAL = 40;
const GAUGE_TICK_POSITIONS = [0, 40, 80, 120, 160, 200];

const clampGaugeValue = (value: number) => {
  if (value == null || Number.isNaN(value)) {
    return GAUGE_MIN;
  }

  return Math.min(Math.max(value, GAUGE_MIN), GAUGE_MAX);
};

const setDecimalValue = (value: number) => {
  if (value == null || Number.isNaN(value)) {
    return '0,0';
  }
  const decimalValue = Math.abs(value).toFixed(1).replace('.', ',');
  return decimalValue === '-0,0' ? '0,0' : decimalValue;
};

export const ElementsRelatedToPhysicalChannelChart = ({ title, className, data, isLoading }: ElementsRelatedToPhysicalChannelChartProps) => {
  const totalPercentage = (data?.percentual?.total ?? 0) * 100;
  const clampedGaugeValue = clampGaugeValue(totalPercentage);

  const graficoData = {
    exporting: {
      enabled: false,
    },
    chart: {
      type: 'gauge',
      backgroundColor: 'transparent',
      height: 280,
    },
    title: {
      text: '',
    },
    pane: {
      startAngle: -90,
      endAngle: 89.9,
      background: null,
      center: ['50%', '75%'],
      size: '100%',
    },
    plotOptions: {
      gauge: {
        wrap: false,
      },
    },
    yAxis: {
      min: GAUGE_MIN,
      max: GAUGE_MAX,
      tickInterval: GAUGE_TICK_INTERVAL,
      tickPositions: GAUGE_TICK_POSITIONS,
      tickLength: 125,
      tickWidth: 0,
      minorTickInterval: null,
      labels: {
        distance: 12,
        style: {
          fontSize: '10px',
        },
        formatter() {
          const label = this.axis.defaultLabelFormatter.call(this);
          return `${label}%`;
        },
      },
      plotBands: [
        {
          from: 0,
          to: 40,
          color: '#D7E8FC',
          thickness: 25,
        },
        {
          from: 40,
          to: 80,
          color: '#A5CCF8',
          thickness: 25,
        },
        {
          from: 80,
          to: 120,
          color: '#559FF2',
          thickness: 25,
        },
        {
          from: 120,
          to: 160,
          color: '#2680EB',
          thickness: 25,
        },
        {
          from: 160,
          to: 200,
          color: '#0D66D0',
          thickness: 25,
        },
      ],
    },
    tooltip: {
      enabled: false,
    },
    series: [
      {
        data: [clampedGaugeValue],
        dataLabels: {
          y: 100,
          borderWidth: 0,
          padding: 14,
          color: '#323232',
          style: {
            fontSize: '32px',
            fontWeight: '600',
            fontFamily: 'Open Sans',
            fontStyle: 'normal',
          },
          formatter() {
            const number = setDecimalValue(totalPercentage);
            return `${number}%`;
          },
        },
        dial: {
          radius: '65%',
          backgroundColor: '#505050',
          baseWidth: 12,
          baseLength: '0%',
          rearLength: '0%',
        },
        pivot: {
          backgroundColor: '#505050',
          radius: 6,
        },
      },
    ],
  };

  return (
    <div className={`${styles['cobertura-total-box']}${className ? ` ${className}` : ''}`}>
      <LoadingSpinerLocal loading={isLoading} size="md" />
      <div className={styles['title-wrapper']}>
        <p className={styles.title}>{title}</p>
        <Whisper
          trigger="hover"
          placement="bottom"
          speaker={<Tooltip>Para calcular a competitividade usamos somente produtos encontrados entre as redes comparadas.</Tooltip>}
        >
          <div>
            <span className={styles['bold-text']}>{formatNumberWithThousandsSeparator(data?.produtos?.comparados)}</span>
            <span className={styles['small-text']}> produtos comparados de </span>
            <span className={styles['bold-text']}>{formatNumberWithThousandsSeparator(data?.produtos?.selecionados)}</span>
            <span className={styles['small-text']}> selecionados</span>
          </div>
        </Whisper>
      </div>
      <div className={styles['gauge-chart-container']}>
        <HighchartsReact highcharts={Highcharts} options={graficoData} />
      </div>
      <div className={styles['bottom-text-wrapper']}>
        <div>
          <span className={styles['regular-text']}>Está em média</span>
          <span className={styles['bold-text']}> {setDecimalValue(data?.percentual?.diff * 100)}% mais</span>
        </div>
        <div>
          <span className={styles['bold-text']}>{data?.percentual?.diff > 0 ? 'caro' : 'barato'} </span>
          <span className={styles['regular-text']}>que o canal físico no período analisado</span>
        </div>
      </div>
    </div>
  );
};
